
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { Badge } from '@/features/shared/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/features/shared/components/ui/avatar';
import { Search, Building2, Mail, Phone, MessageSquare, Loader2, Crosshair, Sparkles } from 'lucide-react';
import { getAllEmployees, getDepartments, getTeamOut, getMyProfile } from '@/lib/hrApi';
import { DANA, openDana } from '@/lib/dana';
import { DanaAvatar } from '@/features/shared/components/branding/DanaAvatar';
import { levelLabel, levelColor } from '../orgLevel';

interface OrgNode {
  id: string;
  name: string;
  position: string;
  orgLevel: string;
  department: string;
  email: string;
  phone: string;
  status: 'active' | 'on_leave' | 'inactive';
  avatar?: string;
  managerId?: string;
  directReports: string[];
}

const INACTIVE_STATUSES = ['inactive', 'terminated', 'suspended'];

export const OrganizationalChart: React.FC = () => {
  const { t } = useTranslation(['employees']);
  const navigate = useNavigate();
  const location = useLocation();
  const goToEmployee = (id: string) => {
    const base = location.pathname.replace(/\/employees(\/.*)?$/, '/employees');
    navigate(`${base}/${id}`);
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [nodes, setNodes] = useState<OrgNode[]>([]);
  const [myId, setMyId] = useState<string | null>(null);
  const [view, setView] = useState<'around' | 'full'>('around');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const findMe = () => {
    if (!myId) return;
    document.getElementById(`org-node-${myId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  };
  // Collapsible tree: only ids in `expanded` show their children. Zoom scales the whole tree.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [zoom, setZoom] = useState(0.8);
  const toggleNode = (id: string) => setExpanded((s) => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const expandAll = () => setExpanded(new Set(nodes.map((n) => n.id)));
  const collapseAll = () => setExpanded(new Set(nodes.filter((n) => !n.managerId).map((n) => n.id)));

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [emps, deptRes, outToday, me] = await Promise.all([
          getAllEmployees(),
          getDepartments().then((d) => d.items || []).catch(() => []),
          // days_ahead=0 → only leave that overlaps today (who's out right now)
          getTeamOut(0).catch(() => []),
          getMyProfile().catch(() => null),
        ]);
        if (!active) return;

        const deptMap = new Map<string, string>((deptRes as any[]).map((d) => [d.id, d.name]));
        const outSet = new Set<string>((outToday as any[]).map((o) => String(o.employee_id)));

        const mapped: OrgNode[] = emps.map((e) => {
          const raw = (e.status || '').toLowerCase();
          let status: OrgNode['status'] = 'active';
          if (INACTIVE_STATUSES.includes(raw)) status = 'inactive';
          // On leave = approved leave covering today (leave-service), never
          // the stored `OnLeave` status, which could outlive the leave.
          else if (e.user_id && outSet.has(String(e.user_id))) status = 'on_leave';

          return {
            id: e.id,
            name: `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email || 'Unknown',
            position: e.position || '—',
            orgLevel: e.org_level || 'employee',
            department: (e.department_id && deptMap.get(e.department_id)) || '',
            email: e.email || '',
            phone: e.phone || '',
            status,
            avatar: e.profile_picture_url || undefined,
            managerId: e.manager_id || undefined,
            directReports: [],
          };
        });

        const ids = new Set(mapped.map((n) => n.id));
        mapped.forEach((n) => {
          // Treat a manager that isn't in the directory as no manager (root).
          if (n.managerId && !ids.has(n.managerId)) n.managerId = undefined;
          n.directReports = mapped.filter((m) => m.managerId === n.id).map((m) => m.id);
        });

        setNodes(mapped);
        const meId = me?.id || null;
        setMyId(meId);
        if (!meId) setView('full'); // no profile match → only the full tree makes sense
        // Expand the top level(s) + the chain from the root down to me, so the
        // current user's card is visible on first load.
        const toExpand = new Set(mapped.filter((n) => !n.managerId).map((n) => n.id));
        if (meId) {
          const byId = new Map(mapped.map((n) => [n.id, n]));
          let cur = byId.get(meId);
          while (cur?.managerId) {
            toExpand.add(cur.managerId);
            cur = byId.get(cur.managerId);
          }
        }
        setExpanded(toExpand);
      } catch {
        if (active) setError(t('employees:org.load_error'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const getStatusColor = (status: OrgNode['status']) => {
    switch (status) {
      case 'active': return 'bg-success-soft text-success-strong border-transparent';
      case 'on_leave': return 'bg-orange-100 dark:bg-orange-500/[0.12] text-orange-800 border-orange-200 dark:border-orange-500/25';
      case 'inactive': return 'bg-danger-soft text-danger-strong border-transparent';
      default: return 'bg-muted text-foreground border-border';
    }
  };
  const getStatusIcon = (status: OrgNode['status']) => {
    switch (status) {
      case 'active': return '🟢';
      case 'on_leave': return '🟡';
      case 'inactive': return '🔴';
      default: return '⚪';
    }
  };
  const initials = (name: string) => name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  // Roots = anyone with no (resolvable) manager — supports an arbitrary-depth
  // chain up to the CEO, and multiple top-level people.
  const roots = useMemo(() => nodes.filter((n) => !n.managerId), [nodes]);

  const searchMatches = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return null;
    return nodes.filter((n) =>
      n.name.toLowerCase().includes(q) ||
      n.position.toLowerCase().includes(q) ||
      n.department.toLowerCase().includes(q),
    );
  }, [searchTerm, nodes]);

  const EmployeeCard = ({ node, hideReports }: { node: OrgNode; hideReports?: boolean }) => {
    const isMe = node.id === myId;
    return (
    <Card
      id={`org-node-${node.id}`}
      className={`w-64 cursor-pointer transition-all hover:shadow-lg border-2 ${
        isMe
          ? 'border-orange-500 ring-2 ring-orange-400/50 dark:ring-orange-500/40'
          : 'border-orange-100 dark:border-orange-500/20 hover:border-orange-300 dark:hover:border-orange-500/40'
      }`}
      onClick={() => goToEmployee(node.id)}
    >
      <CardContent className="px-4 pb-4 pt-6">
        <div className="flex flex-col items-center text-center space-y-3">
          <Avatar className="w-14 h-14 border-2 border-orange-200 dark:border-orange-500/25">
            <AvatarImage src={node.avatar} alt={node.name} />
            <AvatarFallback className="bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700">{initials(node.name)}</AvatarFallback>
          </Avatar>
          <div className="space-y-1">
            <h3 className="font-semibold text-foreground">{node.name}</h3>
            <p className="text-sm text-foreground">{node.position}</p>
            {node.department && <p className="text-xs text-muted-foreground">{node.department}</p>}
            <div className="flex items-center justify-center gap-1 flex-wrap">
              {isMe && <Badge className="text-[10px] bg-orange-500 text-white border border-orange-500">{t('employees:org.you')}</Badge>}
              <Badge className={`text-[10px] border ${levelColor(node.orgLevel)}`}>{levelLabel(node.orgLevel)}</Badge>
              <Badge dot variant={node.status === 'active' ? 'success' : node.status === 'on_leave' ? 'warning' : 'danger'}>
                {t(`employees:status.${node.status}`)}
              </Badge>
            </div>
          </div>
          <div className="flex gap-1">
            {node.email && (
              <a href={`mailto:${node.email}`} aria-label={`Email ${node.name}`} title={`Email ${node.name}`} onClick={(e) => e.stopPropagation()}>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-orange-700 hover:bg-orange-100 dark:hover:bg-orange-500/[0.16]"><Mail className="w-3 h-3" /></Button>
              </a>
            )}
            {node.phone && (
              <a href={`tel:${node.phone}`} aria-label={`Call ${node.name}`} title={`Call ${node.name}`} onClick={(e) => e.stopPropagation()}>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-orange-700 hover:bg-orange-100 dark:hover:bg-orange-500/[0.16]"><Phone className="w-3 h-3" /></Button>
              </a>
            )}
            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-orange-700 hover:bg-orange-100 dark:hover:bg-orange-500/[0.16]"><MessageSquare className="w-3 h-3" /></Button>
          </div>
          {!hideReports && node.directReports.length > 0 && (
            <div className="text-xs text-muted-foreground">👥 {node.directReports.length} {node.directReports.length === 1 ? t('employees:org.report') : t('employees:org.reports')}</div>
          )}
        </div>
      </CardContent>
    </Card>
    );
  };

  // Focused view: just your manager → you → your direct reports.
  const renderAroundMe = () => {
    const me = nodes.find((n) => n.id === myId);
    if (!me) {
      return <div className="py-12 text-center text-muted-foreground">{t('employees:org.not_in_directory')}</div>;
    }
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const manager = me.managerId ? byId.get(me.managerId) : null;
    const myReports = nodes.filter((n) => n.managerId === me.id);
    const lbl = 'text-[11px] font-semibold text-muted-foreground uppercase tracking-wide';
    return (
      <div className="flex flex-col items-center gap-2 py-2">
        {manager && (
          <>
            <span className={lbl}>{t('employees:org.your_manager')}</span>
            <EmployeeCard node={manager} hideReports />
            <div className="w-px h-8 bg-orange-300 dark:bg-orange-500/40" />
          </>
        )}
        <EmployeeCard node={me} hideReports />
        {myReports.length > 0 && (
          <>
            <div className="w-px h-8 bg-orange-300 dark:bg-orange-500/40" />
            <span className={lbl}>{myReports.length === 1 ? t('employees:org.direct_one', { count: myReports.length }) : t('employees:org.direct_other', { count: myReports.length })}</span>
            <div className="flex justify-center gap-6 flex-wrap mt-1">
              {myReports.map((r) => <EmployeeCard key={r.id} node={r} hideReports />)}
            </div>
          </>
        )}
        {!manager && myReports.length === 0 && (
          <p className="text-sm text-muted-foreground mt-2">{t('employees:org.not_linked')}</p>
        )}
      </div>
    );
  };

  const renderOrgNode = (node: OrgNode) => {
    const children = nodes.filter((emp) => emp.managerId === node.id);
    const isOpen = expanded.has(node.id);
    return (
      <div key={node.id} className="flex flex-col items-center space-y-2">
        <EmployeeCard node={node} hideReports />
        {children.length > 0 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); toggleNode(node.id); }}
            className="text-xs px-2 py-0.5 rounded-full border border-orange-200 dark:border-orange-500/25 bg-orange-50 dark:bg-orange-500/[0.08] text-orange-700 hover:bg-orange-100 dark:hover:bg-orange-500/[0.16]"
          >
            {isOpen ? '▾' : '▸'} {children.length} {children.length === 1 ? t('employees:org.report') : t('employees:org.reports')}
          </button>
        )}
        {children.length > 0 && isOpen && (
          <div className="relative pt-4">
            <div className="absolute top-0 left-1/2 w-px h-6 bg-orange-300 transform -translate-x-1/2" />
            <div className="flex justify-center gap-8 mt-2">
              {children.map((child) => (
                <div key={child.id} className="relative">
                  <div className="absolute -top-6 left-1/2 w-px h-6 bg-orange-300 transform -translate-x-1/2" />
                  {children.length > 1 && <div className="absolute -top-6 left-0 right-0 h-px bg-orange-300" />}
                  {renderOrgNode(child)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <Card className="border-2 border-orange-100 dark:border-orange-500/20">
        <CardHeader className="bg-gradient-to-r from-orange-50 dark:from-orange-500/[0.08] to-amber-50 dark:to-amber-500/[0.05] rounded-t-xl">
          <CardTitle className="flex items-center gap-2 text-orange-700">
            <Building2 className="w-5 h-5 text-orange-600" />
            {t('employees:tabs.org_chart')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-orange-400 w-4 h-4" />
            <Input
              placeholder={t('employees:search_employees')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-orange-200 dark:border-orange-500/25 focus:border-orange-400 focus:ring-orange-400"
            />
          </div>

          {/* Legend */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-orange-700 bg-orange-50 dark:bg-orange-500/[0.08] p-3 rounded-lg border border-orange-200 dark:border-orange-500/25">
            <span className="font-medium">{t('employees:org.legend')}</span>
            <span className="flex items-center gap-1">🟢 {t('employees:status.active')}</span>
            <span className="flex items-center gap-1">🟡 {t('employees:status.on_leave')}</span>
            <span className="flex items-center gap-1">🔴 {t('employees:status.inactive')}</span>
          </div>

          {/* Dana — AI HR Assistant: always-available virtual team member, pinned
              above the reporting tree (not part of the manager hierarchy). */}
          <div
            className="flex items-center gap-3 rounded-xl border-2 border-orange-200 dark:border-orange-500/30 bg-gradient-to-r from-orange-50 dark:from-orange-500/[0.1] to-amber-50 dark:to-amber-500/[0.06] p-3 cursor-pointer hover:border-orange-300"
            onClick={() => goToEmployee(DANA.id)}
            role="button"
          >
            <DanaAvatar size={44} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="font-semibold text-foreground">{DANA.name}</span>
                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-orange-600 bg-orange-100 dark:bg-orange-500/[0.16] rounded-full px-1.5 py-0.5">
                  <Sparkles className="w-2.5 h-2.5" /> AI
                </span>
              </div>
              <p className="text-xs text-muted-foreground">{DANA.position} · {t('employees:org.available_to_everyone', { defaultValue: 'Available to everyone' })}</p>
            </div>
            <Button
              size="sm"
              onClick={(e) => { e.stopPropagation(); openDana(); }}
              className="bg-orange-500 hover:bg-orange-600 text-white shrink-0"
            >
              <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> {t('employees:org.chat', { defaultValue: 'Chat' })}
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> {t('employees:org.loading')}
            </div>
          ) : error ? (
            <div className="py-12 text-center text-danger">{error}</div>
          ) : nodes.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">{t('employees:org.no_employees')}</div>
          ) : searchMatches ? (
            // Search results: flat grid of matching people
            searchMatches.length === 0 ? (
              <div className="py-12 text-center text-muted-foreground">{t('employees:org.no_match', { term: searchTerm })}</div>
            ) : (
              <div className="flex flex-wrap gap-6 justify-center py-4">
                {searchMatches.map((n) => <EmployeeCard key={n.id} node={n} />)}
              </div>
            )
          ) : (
            // Org chart: "Around me" focused view or the full collapsible tree
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="inline-flex rounded-lg border border-orange-200 dark:border-orange-500/25 p-0.5 bg-cream">
                  {myId && (
                    <button type="button" onClick={() => setView('around')} className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${view === 'around' ? 'bg-orange-500 text-white' : 'text-charcoal-mid hover:text-orange-600'}`}>
                      {t('employees:org.around_me')}
                    </button>
                  )}
                  <button type="button" onClick={() => setView('full')} className={`text-xs px-3 py-1.5 rounded-md font-medium transition-colors ${view === 'full' ? 'bg-orange-500 text-white' : 'text-charcoal-mid hover:text-orange-600'}`}>
                    {t('employees:org.full_org')}
                  </button>
                </div>
                {view === 'full' && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {myId && (
                      <button type="button" onClick={findMe} className="text-xs px-3 py-1.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white inline-flex items-center gap-1.5">
                        <Crosshair className="w-3.5 h-3.5" /> {t('employees:org.find_me')}
                      </button>
                    )}
                    <button type="button" onClick={expandAll} className="text-xs px-2 py-1 rounded border border-orange-200 dark:border-orange-500/25 text-orange-700 hover:bg-orange-50">{t('employees:org.expand_all')}</button>
                    <button type="button" onClick={collapseAll} className="text-xs px-2 py-1 rounded border border-orange-200 dark:border-orange-500/25 text-orange-700 hover:bg-orange-50">{t('employees:org.collapse_all')}</button>
                    <span className="mx-1 h-4 w-px bg-slate-200" />
                    <button type="button" aria-label="Zoom out" onClick={() => setZoom((z) => Math.max(0.4, +(z - 0.1).toFixed(2)))} className="text-sm w-7 h-7 rounded border border-orange-200 dark:border-orange-500/25 text-orange-700 hover:bg-orange-50">−</button>
                    <span className="text-xs text-muted-foreground w-10 text-center">{Math.round(zoom * 100)}%</span>
                    <button type="button" aria-label="Zoom in" onClick={() => setZoom((z) => Math.min(1.2, +(z + 0.1).toFixed(2)))} className="text-sm w-7 h-7 rounded border border-orange-200 dark:border-orange-500/25 text-orange-700 hover:bg-orange-50">+</button>
                  </div>
                )}
              </div>

              {/* Bounded, pannable viewport. CSS `zoom` (unlike transform:scale)
                  shrinks the layout box too, so the page never blows up. */}
              <div className="overflow-auto max-h-[68vh] rounded-xl border border-border bg-muted/20 p-8">
                {view === 'around' && myId ? (
                  renderAroundMe()
                ) : (
                  <div className="min-w-max flex flex-col items-center gap-12" style={{ zoom } as React.CSSProperties}>
                    {roots.map((root) => renderOrgNode(root))}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
